/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable react/jsx-key */
/* eslint-disable react/display-name */
// TODO: document all this component and give storybook examples, especially about sorting
import ChevronDownIcon from "@heroicons/react/outline/ChevronDownIcon";
import ChevronUpIcon from "@heroicons/react/outline/ChevronUpIcon";
import _ from "lodash";
import * as React from "react";
import type {
  Column,
  TableState,
  TableToggleAllRowsSelectedProps,
  TableToggleRowsSelectedProps,
} from "react-table";
import { useRowSelect, useSortBy, useTable } from "react-table";
import { useVirtual } from "react-virtual";
import type { CSS } from "~/config/stitches";
import { Box } from "~/design-system/Box";
import { useBreakpoint } from "~/design-system/hooks";
import { Icon } from "~/design-system/Icon";
import { Table } from "~/design-system/Table";
import type { SortType } from "../screens/Project/Docs/screens/ProjectDocumentsScreen/components/FilesFolders/components/FilesFoldersList/FilesSort.store";
import { StyledIndeterminateCheckbox } from "./IndeterminateCheckbox";

type rowProps<D> = {
  css?: CSS;
  onClick?: (row: D) => unknown;
};

type rowPropsGetter<D> = (row: D) => rowProps<D> | undefined;

type CheckBoxesRef = {
  [key: string]: React.RefObject<HTMLInputElement | null | undefined>;
};

type VirtualTableProps<D extends object> = {
  css?: CSS;
  noHeader?: boolean;
  data: Array<D>;
  columns: Array<Column<D>>;
  estimateSize?: (index: number) => number;
  initialState?: Partial<TableState<D>>;
  rowProps?: rowProps<D> | rowPropsGetter<D>;
  setSelectedItems?: (selectedItems: D[]) => void;
  canSelectItems?: boolean;
  setClearSelection?: (clearSelection: boolean) => void;
  clearSelection?: boolean;
  onSort?: (sortBy?: SortType) => void;
  manualSort?: boolean;
  setSortedDirection?: React.Dispatch<React.SetStateAction<boolean>>;
  rowWrapperComponent?: React.FC<{ data: D }>;
  getRowKey?: (row: D) => number | string;
};

const MemoizedCheckboxCell = React.memo<{
  checkboxesRef?: CheckBoxesRef;
  getToggleRowSelectedProps: (
    props?: Partial<TableToggleRowsSelectedProps> & {
      ref?: React.RefObject<HTMLInputElement>;
    }
  ) => TableToggleRowsSelectedProps & {
    ref?: React.RefObject<HTMLInputElement>;
  };
  index: number;
  isSelected: boolean;
}>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ checkboxesRef, getToggleRowSelectedProps, index, isSelected }) => {
    const toggleProps = React.useMemo(
      () =>
        getToggleRowSelectedProps({
          ref: checkboxesRef?.[`checkbox${index + 1}`] as
            | React.RefObject<HTMLInputElement>
            | undefined,
        }),
      [getToggleRowSelectedProps, checkboxesRef, index]
    );

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
      },
      []
    );

    return (
      <div
        style={{
          cursor: "pointer",
          display: "inline-block",
          padding: "20px 14px",
          position: "relative",
        }}
      >
        <Box
          padding="xsmall"
          display="flex"
          justifyContent="center"
          style={{ position: "relative", zIndex: 1 }}
        >
          <StyledIndeterminateCheckbox
            size="xsmall"
            onClick={handleClick}
            {...toggleProps}
          />
        </Box>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if this specific checkbox's selection state changed
    if (prevProps.isSelected !== nextProps.isSelected) {
      return false;
    }

    // Re-render if index changed (virtualization)
    if (prevProps.index !== nextProps.index) {
      return false;
    }

    // Skip re-render for all other cases (like when other checkboxes change)
    return true;
  }
);

const useVirtualTable = <D extends object>(
  props: VirtualTableProps<D>,
  ref?: CheckBoxesRef
) => {
  const { breakpoint } = useBreakpoint();

  const tableInstance = useTable(
    {
      columns: props.columns,
      data: props.data,
      initialState: props.initialState,
      /* activate manualSortBy only if we pass our own sorting function */
      manualSortBy: props.manualSort ?? props.onSort !== undefined,
    },
    useSortBy,
    /* allow row selection with useRowSelect and the below rendering function */
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => {
        return [
          {
            Cell: ({
              row: { getToggleRowSelectedProps, index, isSelected },
            }: {
              row: {
                getToggleRowSelectedProps: (
                  props?: Partial<TableToggleRowsSelectedProps> & {
                    ref?: React.RefObject<HTMLInputElement>;
                  }
                ) => TableToggleRowsSelectedProps & {
                  ref?: React.RefObject<HTMLInputElement>;
                };
                index: number;
                isSelected: boolean;
              };
            }) => (
              <MemoizedCheckboxCell
                getToggleRowSelectedProps={getToggleRowSelectedProps}
                index={index}
                isSelected={isSelected}
                checkboxesRef={ref}
              />
            ),
            Header: ({
              getToggleAllRowsSelectedProps,
            }: {
              getToggleAllRowsSelectedProps: (
                props?: Partial<TableToggleAllRowsSelectedProps>
              ) => TableToggleAllRowsSelectedProps;
            }) => (
              <Box
                padding="xsmall"
                paddingLeft="medium"
                display="flex"
                justifyContent="center"
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.stopPropagation();
                  getToggleAllRowsSelectedProps?.().onChange?.(
                    e as unknown as React.ChangeEvent<HTMLElement>
                  );
                }}
              >
                <StyledIndeterminateCheckbox
                  size="xsmall"
                  {...getToggleAllRowsSelectedProps()}
                />
              </Box>
            ),

            id: "selection",
            width: "48px",
          },
          ...columns,
        ];
      });
    }
  );

  const {
    allColumns,
    getTableBodyProps,
    getTableProps,
    headerGroups,
    prepareRow,
    rows,
    selectedFlatRows,
    state: { sortBy },
    toggleAllRowsSelected,
  } = tableInstance;

  /* on sortBy change, fire our sorting function */
  const { onSort } = props;
  React.useEffect(() => {
    let sort: SortType | undefined;
    if (sortBy?.[0]?.desc) {
      sort = "desc";
    } else if (sortBy?.[0]) {
      sort = "asc";
    } else {
      sort = undefined;
    }
    onSort?.(sort);
  }, [onSort, sortBy]);

  const parentRef = React.useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtual({
    estimateSize: props.estimateSize,
    overscan: 10,
    parentRef,
    size: rows.length,
  });

  /* check canSelectItems value and the selection column visibility */
  /* set visible/hidden if necessary */
  const selectionColumn = _.find(allColumns, ["id", "selection"]);
  if (selectionColumn?.isVisible && !props.canSelectItems) {
    selectionColumn?.toggleHidden();
  } else if (!selectionColumn?.isVisible && props.canSelectItems) {
    selectionColumn?.toggleHidden();
  }

  /* hide for docsLabels column on tablet breakpoint */
  const docsLabelsColumn = _.find(allColumns, ["id", "docsLabels"]);
  if (breakpoint === "tablet" && docsLabelsColumn?.isVisible) {
    docsLabelsColumn.toggleHidden();
  } else if (breakpoint !== "tablet" && !docsLabelsColumn?.isVisible) {
    docsLabelsColumn?.toggleHidden();
  }

  const {
    canSelectItems,
    clearSelection,
    setClearSelection,
    setSelectedItems,
  } = props;

  /* update selectedFlatRows in store when changed */
  React.useEffect(() => {
    if (selectedFlatRows && canSelectItems) {
      const debouncedUpdate = _.debounce(() => {
        const items = selectedFlatRows.map((item) => item.original);
        setSelectedItems?.(items);
      }, 50);

      debouncedUpdate();

      return () => {
        debouncedUpdate.cancel();
      };
    }
  }, [canSelectItems, selectedFlatRows, setSelectedItems]);

  /* Reset selected items */
  React.useEffect(() => {
    if (clearSelection) {
      toggleAllRowsSelected(false);
      setClearSelection?.(false);
    }
  }, [clearSelection, setClearSelection, toggleAllRowsSelected]);

  const paddingTop =
    rowVirtualizer.virtualItems.length > 0
      ? rowVirtualizer.virtualItems[0].start
      : 0;
  const paddingBottom =
    rowVirtualizer.virtualItems.length > 0
      ? rowVirtualizer.totalSize -
        rowVirtualizer.virtualItems[rowVirtualizer.virtualItems.length - 1].end
      : 0;

  const actions = React.useMemo(
    () => ({
      getTableBodyProps,
      getTableProps,
      prepareRow,
    }),
    [getTableBodyProps, getTableProps, prepareRow]
  );

  const state = React.useMemo(
    () => ({
      breakpoint,
      headerGroups,
      paddingBottom,
      paddingTop,
      rowVirtualizer,
      rows,
      selectedFlatRows,
    }),
    [
      breakpoint,
      headerGroups,
      paddingBottom,
      paddingTop,
      rowVirtualizer,
      rows,
      selectedFlatRows,
    ]
  );

  return {
    actions,
    refs: {
      parentRef,
    },
    state,
  };
};

const MemoizedTableCell = React.memo<{
  cell: {
    column: { id: string; align?: string };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getCellProps: () => any;
    render: (type: string) => React.ReactNode;
    value: unknown;
    row: { isSelected: boolean };
  };
  canSelectItems?: boolean;
  breakpoint: string;
}>(({ breakpoint, canSelectItems, cell }) => {
  const cellCSS = React.useMemo(
    () => ({
      paddingLeft: canSelectItems ? "0" : "1.5rem",
      ...(cell.column.id === "selection" && {
        padding: 0,
      }),
      ...(cell.column.id === "name" &&
        breakpoint === "mobile" && {
          paddingRight: "1rem",
        }),
    }),
    [canSelectItems, cell.column.id, breakpoint]
  );

  return (
    <Table.Cell
      css={cellCSS}
      {...cell?.getCellProps()}
      align={cell.column.align}
    >
      {cell.render("Cell")}
    </Table.Cell>
  );
});

const TableRow = React.memo<{
  breakpoint: string;
  canSelectItems?: boolean;
  row: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getRowProps: () => any;
    isSelected: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    original: any;
    cells: Array<{
      column: { id: string; align?: string };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getCellProps: () => any;
      render: (type: string) => React.ReactNode;
      value: unknown;
      row: { isSelected: boolean };
    }>;
  };
  rowProps?: {
    css?: CSS;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onClick?: (row: any) => void;
  };
  virtualRow: {
    measureRef: React.RefCallback<HTMLElement>;
    index: number;
  };
  isSelected: boolean;
  entityId: string;
  rowOriginal: Record<string, unknown>;
}>(
  ({ breakpoint, canSelectItems, isSelected, row, rowProps, virtualRow }) => {
    const rowCSS = React.useMemo(
      () =>
        ({
          ...(isSelected && {
            backgroundColor: "$gray50",
          }),
          ...rowProps?.css,
        } as CSS),
      [isSelected, rowProps?.css]
    );

    const handleClick = React.useCallback(() => {
      rowProps?.onClick?.(row.original);
    }, [rowProps?.onClick, row.original]);

    return (
      <Table.Row
        {...row?.getRowProps()}
        css={rowCSS}
        clickable={Boolean(rowProps?.onClick)}
        onClick={handleClick}
        ref={virtualRow.measureRef}
      >
        {row.cells.map((cell) => (
          <MemoizedTableCell
            key={cell.column.id}
            cell={cell}
            canSelectItems={canSelectItems}
            breakpoint={breakpoint}
          />
        ))}
      </Table.Row>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.virtualRow.index !== nextProps.virtualRow.index) {
      return false;
    }

    if (prevProps.entityId !== nextProps.entityId) {
      return false;
    }

    if (prevProps.isSelected !== nextProps.isSelected) {
      return false;
    }

    if (prevProps.canSelectItems !== nextProps.canSelectItems) {
      return false;
    }

    if (prevProps.breakpoint !== nextProps.breakpoint) {
      return false;
    }

    const areRowOriginalsEqual = _.isEqual(
      prevProps.rowOriginal,
      nextProps.rowOriginal
    );

    return areRowOriginalsEqual;
  }
);

const VirtualTable = React.forwardRef(
  VirtualTableRef as unknown as React.ForwardRefRenderFunction<
    unknown,
    VirtualTableProps<object>
  >
) as <T extends object>(
  props: VirtualTableProps<T> & {
    ref?: CheckBoxesRef;
  }
) => JSX.Element;

function VirtualTableRef<D extends object>(
  props: VirtualTableProps<D>,
  ref?: CheckBoxesRef
) {
  const { actions, refs, state } = useVirtualTable(props, ref);
  return (
    <Table.Root
      ref={refs.parentRef}
      fixed
      css={props.css}
      style={{
        "--virtualPaddingBottom": `${state.paddingBottom}px`,
        "--virtualPaddingTop": `${state.paddingTop}px`,
      }}
      {...actions.getTableProps()}
    >
      {props.noHeader ? (
        // If no header, we display a colgroup to set our cells width
        state.headerGroups.map((headerGroup, idx) => {
          return (
            <colgroup key={`list-header-${idx}`}>
              {headerGroup.headers.map((column) => {
                const hasWidth =
                  column.width && typeof column.width === "string";
                return (
                  <col
                    key={column.id}
                    style={{
                      ...(hasWidth && {
                        width: column.width,
                      }),
                      ...(column.basis &&
                        !hasWidth && {
                          width: `${
                            (column.basis / headerGroup.headers.length) * 100
                          }%`,
                        }),
                    }}
                  />
                );
              })}
            </colgroup>
          );
        })
      ) : (
        <Table.Head
          css={{
            position: "sticky",
            top: "0",
            zIndex: "1",
          }}
        >
          {state.headerGroups.map((headerGroup) => (
            <Table.Row {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => {
                const hasWidth =
                  column.width && typeof column.width === "string";
                return (
                  <Table.Cell
                    css={{
                      paddingLeft: props.canSelectItems ? "0" : "1.5rem",
                      ...(column.id === "selection" && {
                        padding: 0,
                      }),
                      ...(column.canSort && {
                        "&:hover": {
                          backgroundColor: "$gray100",
                        },
                      }),
                      ...(hasWidth && {
                        width: column.width,
                      }),
                      ...(column.basis &&
                        !hasWidth && {
                          width: `${
                            (column.basis / headerGroup.headers.length) * 100
                          }%`,
                        }),
                    }}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render("Header")}
                    {!column.disableSortBy && column.isSorted ? (
                      <Icon
                        css={{
                          display: "inline-block",
                          marginLeft: "1ch",
                          verticalAlign: "bottom",
                        }}
                        label={column.isSortedDesc ? "sort desc" : "sort asc"}
                        size="xsmall"
                      >
                        {column.isSortedDesc ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )}
                        {props.setSortedDirection
                          ? props.setSortedDirection(!!column.isSortedDesc)
                          : null}
                      </Icon>
                    ) : null}
                  </Table.Cell>
                );
              })}
            </Table.Row>
          ))}
        </Table.Head>
      )}
      <Table.Body
        css={{
          "&::after": {
            content: '""',
            display: "block",
            paddingBottom: "var(--virtualPaddingBottom)",
          },
          "&::before": {
            content: '""',
            display: "block",
            paddingTop: "var(--virtualPaddingTop)",
          },
        }}
        {...actions.getTableBodyProps()}
      >
        {state.rowVirtualizer.virtualItems.map((virtualRow) => {
          const row = state.rows[virtualRow.index];
          actions.prepareRow(row);
          // If our rowProps depends on the row data
          // retrieve it from a function, if not, just apply the rowProps to all the rows
          const rowProps =
            props.rowProps instanceof Function
              ? props.rowProps(row.original)
              : props.rowProps;

          let entityId = "";

          const original = _.cloneDeep({
            ...(row.original as unknown as {
              type?: string;
              folder?: { id: string };
              file?: { id: string };
              id?: string;
            }),
          });
          if (original?.type === "folder" && original?.folder?.id) {
            entityId = String(original.folder.id);
          } else if (original?.type === "file" && original?.file?.id) {
            entityId = String(original.file.id);
          } else if ("id" in row.original && original?.id) {
            entityId = String(original.id);
          } else {
            entityId = String(virtualRow.index);
          }

          const memoizedRow = (
            <TableRow
              key={`memoized_row_${entityId}`}
              row={row}
              rowProps={rowProps}
              virtualRow={virtualRow}
              breakpoint={state.breakpoint}
              canSelectItems={props.canSelectItems}
              isSelected={row.isSelected}
              entityId={entityId}
              rowOriginal={original}
            />
          );

          return props.rowWrapperComponent ? (
            <props.rowWrapperComponent
              data={row.original}
              key={`memoized_row_wrapper_${entityId}`}
            >
              {memoizedRow}
            </props.rowWrapperComponent>
          ) : (
            memoizedRow
          );
        })}
      </Table.Body>
    </Table.Root>
  );
}

export type { CheckBoxesRef, VirtualTableProps };
export { VirtualTable };
